import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { Leaf } from "lucide-react";
import GoogleAuthButton from "../components/GoogleAuthButton";

export default function SignupPage() {
  const { signup } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/account";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast({ title: "Missing details", description: "Please fill in all fields.", variant: "destructive" });
      return;
    }
    if (password.length < 6) {
      toast({ title: "Password too short", description: "Use at least 6 characters.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await signup(name, email, password);
      toast({ title: "Account created", description: `Welcome, ${name}!` });
      navigate(from, { replace: true });
    } catch (err) {
      toast({
        title: "Signup failed",
        description: err?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-[75vh] bg-gradient-to-b from-green-50 to-white">
      <div className="max-w-md mx-auto px-4 py-16">
        <div className="flex items-center gap-2 justify-center mb-6">
          <span className="w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center">
            <Leaf size={18} />
          </span>
          <span className="font-extrabold text-xl">Fit Kiwi</span>
        </div>
        <div className="rounded-2xl border border-neutral-200 bg-white p-7 shadow-sm">
          <h1 className="text-2xl font-extrabold text-neutral-900">Create your account</h1>
          <p className="mt-1 text-sm text-neutral-600">Start ordering fresh, healthy meals today.</p>

          <div className="mt-6">
            <GoogleAuthButton label="Sign up with Google" />
            <div className="my-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-neutral-200" />
              <span className="text-xs text-neutral-500 font-medium uppercase tracking-wider">or</span>
              <div className="h-px flex-1 bg-neutral-200" />
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Priya Sharma"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                className="mt-1.5"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-11 rounded-full bg-green-600 hover:bg-green-700 text-white"
            >
              {submitting ? "Creating account…" : "Create Account"}
            </Button>
          </form>

          <p className="mt-5 text-sm text-neutral-600 text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-green-700 font-semibold hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </main>
  );
}
